import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { DEFAULT_PAGE_LIMIT, ManageClauseRequestSchema, type LibraryClause, type SearchBoostListResponse } from '@clm/shared-types';

import { fetchBoosts, setClauseBoost } from '../../api/search-quality';
import { CursorPager } from '../../components/CursorPager';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { ErrorNotice } from '../../components/ErrorNotice';
import { EMPTY_CURSOR_STACK, pageNumber, popCursor, pushCursor, type CursorStack } from '../../components/cursor-stack';
import { panelProblemAction, type PanelProblemState } from './panel-action';
import { toProblem, type Panel } from './panel-state';

/**
 * Boosts — manual authority over ranking, and the reason for each — WP-09 §8,
 * NFR-13 · CLM-39, D-B10.
 *
 * Lists current clauses whose `boost_weight` is non-zero with the `boost_reason`
 * migration 0085 records, and offers "Set a boost": clause code, weight
 * (−100..100), reason (1..500). The contract's own union member is the
 * client-side validation (`safeParse` before any request); a 409 is the neutral
 * "already at that weight with that reason", never an error. Success shows the
 * receipt and re-fetches the list. A boost is written in place — no new version
 * — so no judgement goes stale because of it.
 */

type FormPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'field_errors'; readonly messages: readonly string[] }
  | { readonly kind: 'unchanged' }
  | { readonly kind: 'set'; readonly clause_code: string; readonly boost_weight: number; readonly audit_id: string }
  | PanelProblemState;

function formatDate(iso: string): string {
  return iso.slice(0, 10);
}

export function BoostsPanel() {
  const [stack, setStack] = useState<CursorStack>(EMPTY_CURSOR_STACK);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [panel, setPanel] = useState<Panel<SearchBoostListResponse>>({ kind: 'loading' });
  const [busy, setBusy] = useState(true);
  const [reloadNonce, setReloadNonce] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const [code, setCode] = useState('');
  const [weight, setWeight] = useState('');
  const [reason, setReason] = useState('');
  const [form, setForm] = useState<FormPhase>({ kind: 'idle' });

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setBusy(true);
    void (async () => {
      try {
        const response = await fetchBoosts(
          { page: cursor === undefined ? { limit: DEFAULT_PAGE_LIMIT } : { limit: DEFAULT_PAGE_LIMIT, cursor } },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setPanel({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPanel(toProblem(error));
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    })();
    return () => {
      controller.abort();
    };
  }, [cursor, reloadNonce]);

  function reload() {
    setReloadNonce((n) => n + 1);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const body: unknown = {
      action: 'set_clause_boost',
      clause_code: code.trim().toUpperCase(),
      boost_weight: weight.trim() === '' ? Number.NaN : Number(weight),
      reason: reason.trim(),
    };
    /* The contract's own schema is the client-side validation: what the API would answer 400 to is rendered, never sent. */
    const parsed = ManageClauseRequestSchema.safeParse(body);
    if (!parsed.success || parsed.data.action !== 'set_clause_boost') {
      setForm({
        kind: 'field_errors',
        messages: parsed.success
          ? ['The action was not set_clause_boost.']
          : parsed.error.issues.map((issue) => {
              const field = issue.path.join('.');
              if (field === 'clause_code') return 'Clause code: 2–32 characters, upper-case letters, digits and hyphens.';
              if (field === 'boost_weight') return 'Weight: a whole number from −100 to 100.';
              if (field === 'reason') return 'Reason: 1–500 characters — a boost with no reason is one nobody can review.';
              return `${field === '' ? 'Boost' : field}: ${issue.message}`;
            }),
      });
      return;
    }
    const request = parsed.data;
    setForm({ kind: 'busy' });
    void (async () => {
      try {
        const response = await setClauseBoost({ clause_code: request.clause_code, boost_weight: request.boost_weight, reason: request.reason });
        setForm({ kind: 'set', clause_code: request.clause_code, boost_weight: request.boost_weight, audit_id: response.audit.audit_id });
        setCode('');
        setWeight('');
        setReason('');
        reload();
      } catch (error) {
        const problem = toProblem(error);
        setForm(problem.problem === 'conflict' ? { kind: 'unchanged' } : problem);
      }
    })();
  }

  const columns: readonly DataTableColumn<LibraryClause>[] = [
    {
      id: 'clause',
      header: 'Clause',
      cell: (item) => (
        <Link to={`/clauses/${encodeURIComponent(item.clause_code)}`}>
          <code>{item.clause_code}</code>
        </Link>
      ),
    },
    { id: 'title', header: 'Title', cell: (item) => item.title },
    {
      id: 'weight',
      header: 'Weight',
      cellClassName: 'num',
      cell: (item) => (
        <span className="sq-weight" data-tone={item.boost_weight < 0 ? 'bad' : 'plain'}>
          {item.boost_weight > 0 ? `+${String(item.boost_weight)}` : String(item.boost_weight)}
        </span>
      ),
    },
    {
      id: 'reason',
      header: 'Reason',
      cell: (item) => (item.boost_reason === null ? <span className="mut">— (recorded before 0085)</span> : item.boost_reason),
    },
    {
      id: 'edited',
      header: 'Last edited',
      cell: (item) =>
        item.last_edited_by_upn === null || item.last_edited_utc === null ? (
          <span className="mut">—</span>
        ) : (
          <>
            {item.last_edited_by_upn}
            <br />
            <span className="mut">{formatDate(item.last_edited_utc)}</span>
          </>
        ),
    },
  ];

  const response = panel.kind === 'ready' ? panel.response : null;
  const items = response?.items ?? [];

  return (
    <section className="card" aria-labelledby="sq-boosts">
      <h3 id="sq-boosts">Boosts</h3>
      <p>
        Manual authority over ranking (λ_b = 0.10 per point). A positive weight pins a clause up, a negative one buries it; the
        reason is recorded with the weight and reaches the audit row. A boost changes no clause text and stales no judgement.
      </p>

      <form className="sq-form" aria-labelledby="sq-boost-form" onSubmit={submit}>
        <h4 id="sq-boost-form">Set a boost</h4>
        <label>
          Clause code
          <input type="text" value={code} required maxLength={32} onChange={(e) => setCode(e.target.value)} />
        </label>
        <label>
          Weight (−100 to 100)
          <input type="number" value={weight} required min={-100} max={100} step={1} onChange={(e) => setWeight(e.target.value)} />
        </label>
        <label>
          Reason
          <textarea value={reason} required maxLength={500} onChange={(e) => setReason(e.target.value)} />
        </label>
        <div className="sq-actions">
          <button type="submit" className="btn" disabled={form.kind === 'busy'}>
            Set boost
          </button>
          {form.kind === 'set' ? (
            <p role="status" className="mut">
              Boost on <code>{form.clause_code}</code> set to {form.boost_weight} · audit <code>{form.audit_id}</code>
            </p>
          ) : null}
          {form.kind === 'unchanged' ? (
            <p role="status" className="mut">
              Already at that weight with that reason — nothing was written.
            </p>
          ) : null}
        </div>
        {form.kind === 'field_errors' ? (
          <ul role="alert" className="mut">
            {form.messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        ) : null}
        {form.kind === 'problem' ? (
          <ErrorNotice
            kind={form.problem}
            detail={form.detail}
            correlation_id={form.correlation_id}
            action={
              form.problem === 'forbidden' ? (
                <p className="mut">
                  Setting a boost is a CEQ-Legal action (<code>legal_reviewer</code>).
                </p>
              ) : form.problem === 'not_implemented' ? (
                <p className="mut">
                  The endpoint is <code>manage-clause</code> (<code>POST /v1/clauses/actions</code>).
                </p>
              ) : undefined
            }
          />
        ) : null}
      </form>

      {panel.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading manual boosts…
        </p>
      ) : null}

      {panel.kind === 'problem' ? (
        <ErrorNotice
          kind={panel.problem}
          detail={panel.detail}
          correlation_id={panel.correlation_id}
          action={panelProblemAction(panel.problem, 'search-boosts (GET /v1/search/boosts)', reload)}
        />
      ) : null}

      {response ? (
        <>
          <div className="totals">
            <div className="total">
              <span>Pinned</span>
              <b>{response.totals.boosted}</b>
            </div>
            <div className="total">
              <span>Buried</span>
              <b>{response.totals.buried}</b>
            </div>
          </div>

          <DataTable
            caption="Manual boosts on current clauses"
            columns={columns}
            rows={items}
            rowKey={(item) => item.clause_code}
            busy={busy}
            empty={
              <p>
                <strong>No clause is boosted or buried.</strong>
              </p>
            }
          />

          {items.length > 0 ? (
            <CursorPager
              next_cursor={response.page.next_cursor}
              historyDepth={stack.cursors.length}
              busy={busy}
              pageNumber={pageNumber(stack)}
              returnedCount={response.page.returned_count}
              totalInScope={response.page.total_in_scope}
              onNext={() => {
                const next = response.page.next_cursor;
                if (next === null) return;
                setStack((current) => pushCursor(current, next));
                setCursor(next);
              }}
              onPrevious={() => {
                const popped = popCursor(stack);
                setStack(popped.stack);
                setCursor(popped.cursor);
              }}
            />
          ) : null}
        </>
      ) : null}
    </section>
  );
}
